import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, IconButton, InputAdornment,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography, Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { modeloService } from '../../../services/fashionService';
import { FashionModelo } from '../../../types/fashion';

const Modelos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<FashionModelo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await modeloService.getModelos({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
      });
      setItems(res.items);
      setTotalCount(res.totalCount);
    } catch {
      enqueueSnackbar('Error al cargar modelos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const handleDelete = async (codigo: string) => {
    if (!window.confirm(`¿Eliminar el modelo ${codigo}?`)) return;
    try {
      await modeloService.deleteModelo(codigo);
      enqueueSnackbar('Modelo eliminado', { variant: 'success' });
      fetchData();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data || 'No se pudo eliminar', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Modelos
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/inventario/fashion/modelos/nuevo')}>
                Nuevo modelo
              </Button>
            </Stack>
            <TextField
              fullWidth
              placeholder="Buscar por código o descripción"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Curva</TableCell>
                        <TableCell>Temporada</TableCell>
                        <TableCell>Variantes</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((m) => (
                        <TableRow key={m.codigoModelo} hover>
                          <TableCell>{m.codigoModelo}</TableCell>
                          <TableCell>{m.descripcion}</TableCell>
                          <TableCell>{m.nombreCurva || m.codigoCurva}</TableCell>
                          <TableCell>{m.nombreTemporada || m.codigoTemporada || '-'}</TableCell>
                          <TableCell>{m.cantidadVariantes}</TableCell>
                          <TableCell>{m.estado === 'A' ? 'Activo' : 'Inactivo'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => navigate(`/inventario/fashion/modelos/${encodeURIComponent(m.codigoModelo)}`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" onClick={() => handleDelete(m.codigoModelo)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {items.length === 0 && (
                        <TableRow><TableCell colSpan={7} align="center">Sin resultados</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  labelRowsPerPage="Filas"
                />
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Modelos;
